import React, { useState } from 'react';
import { useCompany } from '../context/CompanyContext';
import { useUser } from '../context/UserContext';
import { appointmentsAPI, ordersAPI, ticketsAPI } from '../api';
import { useFetch } from '../hooks/useFetch';
import { Card, Badge, LoadingView, ErrorView, EmptyView } from '../components/UI';
import { fmt } from '../utils/formatters';
import { statusColor } from '../theme/colors';

function TicketCard({ ticket }) {
    const sc = statusColor(ticket.status);
    const isEvent = ticket.type === 'event';
    const isFlight = ticket.type === 'flight';
    return (
        <Card style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>{isEvent ? '🎟' : isFlight ? '✈️' : '🎫'}</span>
                <div style={{ flex: 1 }}>
                    <p style={{
                        fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                        color: 'var(--text-primary)', marginBottom: 2
                    }}>
                        {isEvent ? (ticket.eventName || 'Event Ticket')
                            : isFlight ? `${ticket.flightNumber} ${ticket.origin}→${ticket.destination}`
                                : 'Ticket'}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {isEvent ? fmt.date(ticket.eventDate)
                            : isFlight ? `${fmt.date(ticket.departureDate)} ${fmt.time(ticket.departureTime)}`
                                : fmt.dateTime(ticket.createdAt)}
                    </p>
                </div>
                <Badge label={ticket.status} bgColor={sc.bg} color={sc.text} borderColor={sc.border} />
            </div>
            {(ticket.seatLabel || ticket.seatClass) && (
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>
                    💺 {ticket.seatLabel || ticket.seatClass}
                </p>
            )}
            {ticket.venue && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>📍 {ticket.venue}</p>}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8
            }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                    #{ticket.ticketId?.slice(-8).toUpperCase()}
                </span>
                {ticket.qrUsed && <span style={{ color: 'var(--brand-300)', fontSize: 12, fontWeight: 600 }}>✓ Scanned</span>}
            </div>
        </Card>
    );
}

function AppointmentCard({ appt }) {
    const sc = statusColor(appt.status);
    return (
        <Card style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>📅</span>
                <div style={{ flex: 1 }}>
                    <p style={{
                        fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                        color: 'var(--text-primary)', marginBottom: 2
                    }}>{appt.service}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {fmt.date(appt.appointmentDate)}{appt.appointmentTime ? ` at ${fmt.time(appt.appointmentTime)}` : ''}
                    </p>
                </div>
                <Badge label={appt.status} bgColor={sc.bg} color={sc.text} borderColor={sc.border} />
            </div>
            {appt.callerName && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>👤 {appt.callerName}</p>}
            {appt.notes && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>📝 {appt.notes}</p>}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8
            }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                    #{appt.confirmationNumber}
                </span>
                {appt.qrUsed && <span style={{ color: 'var(--brand-300)', fontSize: 12, fontWeight: 600 }}>✓ Checked in</span>}
            </div>
            {appt.paymentUrl && (
                <a href={appt.paymentUrl} target="_blank" rel="noreferrer" style={{
                    display: 'block', marginTop: 8, backgroundColor: '#422006', border: '1px solid #f59e0b',
                    borderRadius: 8, padding: '8px 12px', color: '#fbbf24', fontSize: 13,
                    fontWeight: 600, textAlign: 'center', textDecoration: 'none',
                }}>💳 Complete Payment →</a>
            )}
        </Card>
    );
}

function OrderCard({ order }) {
    const sc = statusColor(order.status);
    const itemCount = (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
    return (
        <Card style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>🛍</span>
                <div style={{ flex: 1 }}>
                    <p style={{
                        fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                        color: 'var(--text-primary)', marginBottom: 2
                    }}>
                        {(order.items || []).slice(0, 2).map(i => i.name).join(', ') || 'Order'}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {fmt.dateTime(order.createdAt)} · {order.fulfillmentType}
                    </p>
                </div>
                <Badge label={order.status} bgColor={sc.bg} color={sc.text} borderColor={sc.border} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                <span style={{ color: 'var(--brand-300)', fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    {fmt.money(order.totalAmount, order.currency?.toUpperCase())}
                </span>
            </div>
            {order.estimatedPickup && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>⏱ Ready: {order.estimatedPickup}</p>}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8
            }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                    #{order.confirmationNumber}
                </span>
                {order.qrUsed && <span style={{ color: 'var(--brand-300)', fontSize: 12, fontWeight: 600 }}>✓ Picked up</span>}
            </div>
            {order.checkoutUrl && order.paymentStatus !== 'paid' && (
                <a href={order.checkoutUrl} target="_blank" rel="noreferrer" style={{
                    display: 'block', marginTop: 8, backgroundColor: '#422006', border: '1px solid #f59e0b',
                    borderRadius: 8, padding: '8px 12px', color: '#fbbf24', fontSize: 13,
                    fontWeight: 600, textAlign: 'center', textDecoration: 'none',
                }}>💳 Complete Payment →</a>
            )}
        </Card>
    );
}

const TABS = [
    { key: 'appointments', label: '📅 Bookings' },
    { key: 'orders', label: '🛍 Orders' },
    { key: 'tickets', label: '🎟 Tickets' },
];

export default function MyBookingsScreen() {
    const { company } = useCompany();
    const { profile } = useUser();
    const [tab, setTab] = useState('appointments');
    const hasPhone = !!profile.phone;

    const { data: apptData, loading: apptLoading, error: apptError, refetch: refetchAppt } =
        useFetch(() => hasPhone
            ? appointmentsAPI.listByPhone(company?.companyId, profile.phone)
            : Promise.resolve({ data: { appointments: [] } }),
            [company?.companyId, profile.phone]);

    const { data: orderData, loading: orderLoading, error: orderError, refetch: refetchOrder } =
        useFetch(() => hasPhone
            ? ordersAPI.listByPhone(company?.companyId, profile.phone)
            : Promise.resolve({ data: { orders: [] } }),
            [company?.companyId, profile.phone]);

    const { data: ticketData, loading: ticketLoading, error: ticketError, refetch: refetchTicket } =
        useFetch(() => hasPhone
            ? ticketsAPI.myTickets(company?.companyId, profile.phone)
            : Promise.resolve({ data: { items: [] } }),
            [company?.companyId, profile.phone]);

    const appointments = apptData?.appointments || [];
    const orders = orderData?.orders || [];
    const tickets = ticketData?.items || ticketData?.tickets || [];

    if (!hasPhone) {
        return (
            <div style={{ padding: 24 }}>
                <EmptyView emoji="📂" title="No Bookings Yet"
                    subtitle="Add your Phone Number in Profile to see your Bookings here." />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Tabs */}
            <div style={{
                display: 'flex', padding: 8, gap: 6, backgroundColor: 'var(--bg-card)',
                borderBottom: '1px solid var(--border)', flexShrink: 0
            }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                        flex: 1, padding: '8px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        backgroundColor: tab === t.key ? 'var(--brand-500)' : 'transparent',
                        color: tab === t.key ? '#fff' : 'var(--text-muted)',
                        fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)',
                    }}>{t.label}</button>
                ))}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {tab === 'appointments' && (
                    apptLoading ? <LoadingView label="Loading Bookings…" />
                        : apptError ? <ErrorView message={apptError} onRetry={refetchAppt} />
                            : appointments.length === 0
                                ? <EmptyView emoji="📅" title="No Appointments" subtitle="Book a Service from the Services tab" />
                                : appointments.map(a => <AppointmentCard key={a.appointmentId} appt={a} />)
                )}
                {tab === 'orders' && (
                    orderLoading ? <LoadingView label="Loading Orders…" />
                        : orderError ? <ErrorView message={orderError} onRetry={refetchOrder} />
                            : orders.length === 0
                                ? <EmptyView emoji="🛍" title="No Orders" subtitle="Browse Products from the Services tab" />
                                : orders.map(o => <OrderCard key={o.orderId} order={o} />)
                )}
                {tab === 'tickets' && (
                    ticketLoading ? <LoadingView label="Loading tickets…" />
                        : ticketError ? <ErrorView message={ticketError} onRetry={refetchTicket} />
                            : tickets.length === 0
                                ? <EmptyView emoji="🎟" title="No Tickets" subtitle="Browse Events and Flights to Buy Tickets" />
                                : tickets.map(t => <TicketCard key={t.ticketId} ticket={t} />)
                )}
            </div>
        </div>
    );
}
